import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MarketingNav, MarketingFooter } from '../../components/marketing/MarketingLayout';
import {
  FileText, Users, Calculator, Receipt, ShoppingBag, BarChart3,
  Landmark, Settings, Bell, Building2, Shield, QrCode, Globe,
  ClipboardCheck, Database, Eye, BookOpen, FileDown, Play,
  CheckCircle2, Lock, UserCheck, ArrowRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const DUBAI_BG = 'https://images.unsplash.com/photo-1726003354173-a1152883ca69?w=1920&q=40';

function Reveal({ children, delay = 0 }) {
  return <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-60px' }} transition={{ duration: 0.6, delay }}>{children}</motion.div>;
}

function ScreenshotLightbox({ src, title }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div className="group cursor-pointer rounded-xl overflow-hidden border border-zinc-200/80 shadow-md hover:shadow-xl hover:-translate-y-0.5 transition-all bg-white" onClick={() => setOpen(true)} data-testid={`feature-screenshot-${title.toLowerCase().replace(/\s+/g, '-')}`}>
        <img src={src} alt={title} className="w-full h-auto transition-transform group-hover:scale-[1.02]" loading="lazy" />
        <div className="px-4 py-2.5 flex items-center justify-between border-t border-zinc-100">
          <span className="text-sm font-medium text-[#0f172a]">{title}</span>
          <span className="text-[10px] text-[#71717A]">Click to expand</span>
        </div>
      </div>
      {open && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 cursor-pointer" onClick={() => setOpen(false)} data-testid="feature-lightbox">
          <img src={src} alt={title} className="max-w-full max-h-[90vh] rounded-lg shadow-2xl" />
          <button className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 text-xl">&times;</button>
        </div>
      )}
    </>
  );
}

const MODULES = [
  { icon: BarChart3, title: 'Dashboard & Analytics', desc: 'Real-time KPIs, revenue trends, expense analysis, financial forecasting with visual charts. AI-powered smart insights and budget management.', screenshots: ['/booklet/02_dashboard.jpeg', '/booklet/45_analytics.jpeg'], color: '#3b82f6' },
  { icon: FileText, title: 'Sales & Invoicing', desc: 'Professional invoices with company branding, tax calculations, QR codes, and digital signatures. Quotation-to-invoice conversion. Editable documents with version history.', screenshots: ['/booklet/06_sales.jpeg', '/booklet/16_invoice_pdf_qr.jpeg'], color: '#10B981' },
  { icon: ShoppingBag, title: 'Purchases & Procurement', desc: 'Purchase Orders, RFQs, Goods Received Notes. Complete procurement lifecycle with multi-level approval gates. Supplier management and tracking.', screenshots: ['/booklet/19_purchases_po.jpeg', '/booklet/20_po_pdf_qr.jpeg'], color: '#f59e0b' },
  { icon: Users, title: 'HR & Workforce', desc: 'Employee management, payroll processing, attendance tracking, leave management. Complete employee lifecycle from onboarding to exit. Non-complicated entries.', screenshots: ['/booklet/03_hr.jpeg', '/booklet/04_payroll.jpeg'], color: '#ec4899' },
  { icon: Calculator, title: 'Accounts & Finance', desc: 'Chart of Accounts, Journal Entries, Trial Balance, Profit & Loss, Balance Sheet. Double-entry accounting built to international standards.', screenshots: ['/booklet/18_accounts_coa.jpeg', '/booklet/43_financial_reports.jpeg'], color: '#8b5cf6' },
  { icon: Landmark, title: 'Tax Compliance (VAT/GST)', desc: 'UAE VAT 5%, Corporate Tax, Indian GST — all built in. Tax period management, filing preparation, return reports. Multi-rate tax configuration.', screenshots: ['/booklet/41_tax_compliance.jpeg', '/booklet/42_tax_compliance_detail.jpeg'], color: '#10B981' },
  { icon: Receipt, title: 'Vouchers & Receipts', desc: 'Payment vouchers, receipt vouchers with proper numbering. Auto-linked to journal entries and financial reports. Full audit trail on every transaction.', screenshots: ['/booklet/38_journal_entries.jpeg'], color: '#f97316' },
  { icon: Building2, title: 'Accountant Multi-Client Mode', desc: 'BizCore Pro™ exclusive. Manage unlimited client companies from a single login. One-click context switching with complete data isolation between clients.', screenshots: ['/booklet/62_accountant_clients_full.jpeg', '/booklet/67_switch_client.jpeg'], color: '#d4a84b' },
];

export default function FeaturesPage() {
  const handleDemo = async () => {
    try {
      const res = await fetch(`${API}/api/auth/demo-login`, { method: 'POST' });
      if (!res.ok) throw new Error();
      const data = await res.json();
      localStorage.setItem('erp_token', data.access_token);
      localStorage.setItem('erp_user', JSON.stringify({ ...data.user, is_demo: true }));
      localStorage.setItem('erp_demo_mode', 'true');
      window.location.href = '/dashboard';
    } catch { alert('Demo unavailable.'); }
  };

  return (
    <div className="bg-[#FCFCFC] text-[#0A0A0A] min-h-screen" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <MarketingNav />

      {/* Hero */}
      <section className="relative pt-28 pb-16 lg:pt-36 lg:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${DUBAI_BG})`, opacity: 0.05 }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#d4a84b] mb-3">Features & Modules</p>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Everything Your Business Needs
            </h1>
            <p className="text-base lg:text-lg text-[#52525B] max-w-2xl mx-auto">
              12+ modules working together seamlessly. User-friendly interfaces, non-complicated entries, 
              full audit logs, and editable features — designed for how businesses actually work.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Module Sections */}
      {MODULES.map((mod, idx) => (
        <section key={idx} className={`py-16 lg:py-24 ${idx % 2 === 0 ? 'bg-white' : 'bg-zinc-50/80'}`} data-testid={`module-${mod.title.toLowerCase().replace(/\s+/g, '-')}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-10 items-center ${idx % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
              <Reveal>
                <div className={idx % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${mod.color}15` }}>
                      <mod.icon size={22} style={{ color: mod.color }} />
                    </div>
                    <h2 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>{mod.title}</h2>
                  </div>
                  <p className="text-sm leading-relaxed text-[#52525B] mb-6">{mod.desc}</p>
                  <button onClick={handleDemo} className="inline-flex items-center gap-2 text-sm font-semibold text-[#0f172a] hover:text-[#d4a84b] transition-colors">
                    <Play size={14} fill="currentColor" /> Try in Demo <ArrowRight size={14} />
                  </button>
                </div>
              </Reveal>
              <Reveal delay={0.15}>
                <div className={`grid ${mod.screenshots.length > 1 ? 'grid-cols-2' : 'grid-cols-1'} gap-4 ${idx % 2 === 1 ? 'lg:order-1' : ''}`}>
                  {mod.screenshots.map((s, i) => (
                    <ScreenshotLightbox key={i} src={s} title={`${mod.title} ${i + 1}`} />
                  ))}
                </div>
              </Reveal>
            </div>
          </div>
        </section>
      ))}

      {/* Training Manual Section */}
      <section id="manual" className="py-20 lg:py-28 bg-gradient-to-br from-[#0f172a] to-[#1e3a5f]" data-testid="manual-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <Reveal>
              <div className="text-white">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#d4a84b] mb-3">Documentation</p>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Built-in Product Manual
                </h2>
                <p className="text-base leading-relaxed text-zinc-300 mb-8">
                  49-page product manual with annotated screenshots of every feature. 
                  Available as an interactive in-app gallery and a downloadable landscape PDF. 
                  Dual editions: BizCore™ (39 pages) and BizCore Pro™ (10 pages).
                </p>
                <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className="p-5 rounded-xl bg-white/5 border border-white/10">
                    <BookOpen size={22} className="text-[#d4a84b] mb-2" />
                    <div className="text-sm font-semibold">Interactive Gallery</div>
                    <div className="text-xs text-zinc-400">Category filters & lightbox</div>
                  </div>
                  <div className="p-5 rounded-xl bg-white/5 border border-white/10">
                    <FileDown size={22} className="text-[#d4a84b] mb-2" />
                    <div className="text-sm font-semibold">PDF Download</div>
                    <div className="text-xs text-zinc-400">49 pages, landscape A4</div>
                  </div>
                </div>
                <button onClick={handleDemo} className="bg-[#d4a84b] text-[#0f172a] hover:bg-[#b8912e] rounded-lg font-semibold px-6 py-3 text-sm transition-colors inline-flex items-center gap-2">
                  <Play size={14} fill="currentColor" /> Open Manual in Demo
                </button>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="grid grid-cols-3 gap-3">
                {['BizCore™', 'BizCore Pro™', '49 Pages', 'Landscape A4', 'QR Codes', 'Dark Mode', 'Tax Reports', 'Audit Stamps', 'HR Module'].map((tag, i) => (
                  <div key={i} className="p-3 rounded-xl bg-white/5 border border-white/10 text-center text-xs font-medium text-zinc-300">{tag}</div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
